/**
 * Small presentational pieces shared by the Results, Developer and File tabs.
 * OWNER: workstream F. Anything reusable beyond these three surfaces belongs in
 * `components/ui/primitives.tsx` instead (owned by W0).
 */

import * as React from "react";
import { Check, Copy, Info, X } from "lucide-react";
import { Button } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { copyText } from "./clipboard";

/** Inline, dismissible message. Used where an action belongs to another tab. */
export function NoticeBar({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded border border-ads-accent-border bg-ads-accent-soft px-3 py-2 text-xs text-ads-text">
      <Info className="mt-px h-3.5 w-3.5 shrink-0 text-ads-accent" />
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded p-0.5 text-ads-accent hover:bg-ads-hover"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Quiet footnote used to say where a number on screen came from. */
export function SourceNote({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn("text-[11px] leading-relaxed text-neutral-500", className)}>{children}</p>;
}

export function KeyValue({
  label,
  value,
  mono,
  title,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  title?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-neutral-500">{label}</span>
      <span
        title={title}
        className={cn("truncate text-xs text-neutral-800", mono && "font-mono text-[11px]")}
      >
        {value}
      </span>
    </div>
  );
}

/** Copy-to-clipboard button that confirms in place for ~1s. */
export function CopyButton({
  text,
  label = "Copy",
  size = "sm",
  className,
}: {
  text: string;
  label?: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      size={size}
      className={className}
      onClick={async () => setCopied(await copyText(text))}
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : label}
    </Button>
  );
}

/**
 * Fixed-width text block. `whitespace-pre` (not pre-wrap) is deliberate: the
 * radiological report and the docker command are column-aligned, and reflowing
 * them destroys the alignment the pipeline authored.
 */
export function MonoBlock({
  text,
  className,
  maxHeight,
}: {
  text: string;
  className?: string;
  maxHeight?: number;
}) {
  return (
    <div
      className={cn("overflow-auto rounded border border-neutral-200 bg-neutral-50", className)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <pre className="whitespace-pre px-3 py-2 font-mono text-[11px] leading-[1.45] text-neutral-800">
        {text}
      </pre>
    </div>
  );
}

/** Section header inside a scrolling tab body. */
export function SectionHeader({
  title,
  hint,
  actions,
}: {
  title: string;
  hint?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <h2 className="text-sm font-semibold text-neutral-800">{title}</h2>
        {hint ? <p className="mt-0.5 text-[11px] text-neutral-500">{hint}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}
